import { useCallback } from 'react';

import {
  createWorkspace,
  getUploadUrl,
  normalizeUploadFileName,
  parseSignedUploadResponse,
  putFileToSignedUrl,
  updateWorkspace,
  uploadWorkspacePhoto,
} from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { computeMd5HexFromBlob } from '../../../../shared/utils/md5Hex';

const TENANT_PHOTO_UPLOAD_FILE_ROUTE = 'tenant_photo';

const logicalNameFromUploadedFileName = (normalizedFileName) => {
  const base = String(normalizedFileName || '').trim();
  const idx = base.lastIndexOf('.');
  if (idx <= 0) return base || 'file';
  const stem = base.slice(0, idx);
  return stem || 'file';
};

const extractCreatedWorkspaceId = (res) => {
  const b = res?.body;
  if (b && typeof b === 'object') {
    const id =
      b.workspaceUuid ??
      b.workspace_uuid ??
      b.workspaceId ??
      b.workspace_id ??
      b.id ??
      null;
    return id != null ? String(id) : null;
  }
  if (typeof b === 'string' && b.trim()) return b.trim();
  return null;
};

const slugForUpload = (explicit, nameFallback) => {
  const s = String(explicit ?? '').trim();
  if (s) return s;
  const n = String(nameFallback ?? '').trim();
  return n ? n.toLowerCase().replace(/\s+/g, '-') : '';
};

const DEFAULT_PHONE = '+1234567890';

/**
 * Core body for workspace create/update (same keys as POST /v1/workspace/create).
 * Update adds `workspace_uuid` when provided.
 *
 * @param {object} p
 * @param {string} [p.workspace_uuid]
 * @param {string} p.name
 * @param {string} p.slug
 * @param {string} p.contact_email
 * @param {string} p.contact_name
 * @param {string} [p.phone_number]
 * @param {string} [p.address] — maps to `contact_address` when non-empty
 * @returns {Record<string, unknown>}
 */
const buildWorkspaceWriteBody = ({
  workspace_uuid,
  name,
  slug,
  contact_email,
  contact_name,
  phone_number,
  address,
}) => {
  const body = {};
  if (workspace_uuid != null && String(workspace_uuid).trim()) {
    body.workspace_uuid = String(workspace_uuid).trim();
  }
  body.name = name;
  body.slug = slug;
  body.contact_email = contact_email;
  body.contact_name = contact_name;
  body.phone_number = String(phone_number ?? '').trim() || DEFAULT_PHONE;
  body.billing_plan = 'basic';
  body.organization_id = 1;
  const addr = String(address ?? '').trim();
  if (addr) body.contact_address = addr;
  return body;
};

/**
 * API flow (with logo): POST /v1/file/upload-url → PUT S3 → POST /v1/workspace/create|update (full write body) → POST /v1/workspace/photo.
 *
 * @typedef {object} CreateWorkspaceFormValues
 * @property {string} trimmedName
 * @property {string} slug
 * @property {string} ownerEmail
 * @property {string} contactName
 * @property {string} [address]
 * @property {File | null} [logoFile]
 *
 * @typedef {object} UpdateWorkspaceFormValues
 * @property {string} workspaceId
 * @property {string} [customerSlug] — tenant slug for upload-url `customer` and `slug` on update body
 * @property {string} trimmedName
 * @property {string} contactEmail — `contact_email` (e.g. owner from GET workspace)
 * @property {string} contactName — `contact_name` (e.g. owner display name from GET workspace)
 * @property {string} [address]
 * @property {string} [phoneNumber] — `phone_number` (falls back to same default as create if empty)
 * @property {File | null} [logoFile]
 *
 * @typedef {object} WorkspaceSaveFlowSuccess
 * @property {'success'} type
 * @property {string | null} workspaceId
 *
 * @typedef {object} WorkspaceSaveFlowPhotoError
 * @property {'photo_error'} type
 * @property {string | null} workspaceId
 * @property {string} message
 */

/**
 * @param {(key: string) => string} t — `useTranslation('workspaces').t`
 */
export function useWorkspaceSaveFlow(t) {
  const executeCreate = useCallback(
    /**
     * @param {CreateWorkspaceFormValues} values
     * @returns {Promise<WorkspaceSaveFlowSuccess | WorkspaceSaveFlowPhotoError>}
     */
    async (values) => {
      const { trimmedName, slug, ownerEmail, contactName, address, logoFile } = values;

      let imageFileName = '';
      let imageLogicalName = '';
      let s3Path = '';

      if (logoFile) {
        imageFileName = normalizeUploadFileName(logoFile.name);
        imageLogicalName = logicalNameFromUploadedFileName(imageFileName);

        const uploadPayload = {
          customer: slug,
          file_name: imageFileName,
          file_type: logoFile.type,
          file_route: TENANT_PHOTO_UPLOAD_FILE_ROUTE,
          overwrite: false,
        };
        const uploadRaw = await getUploadUrl(uploadPayload);
        validateApiResponse(uploadRaw, t('list.createModal.submitError'));
        const { uploadUrl, s3Path: resolvedS3Path } = parseSignedUploadResponse(uploadRaw);
        if (!resolvedS3Path || !String(resolvedS3Path).trim()) {
          throw new Error('Missing s3_path in upload URL response');
        }
        s3Path = String(resolvedS3Path).trim();

        await putFileToSignedUrl(uploadUrl, logoFile);
      }

      const body = buildWorkspaceWriteBody({
        name: trimmedName,
        slug,
        contact_email: ownerEmail,
        contact_name: contactName,
        phone_number: DEFAULT_PHONE,
        address,
      });

      if (logoFile) {
        body.logical_name = imageLogicalName;
        body.filename = imageFileName;
        body.file_type = logoFile.type;
        body.path = s3Path;
      }

      const createRes = await createWorkspace(body);
      const normalizedCreate = validateApiResponse(createRes, t('list.createModal.submitError'));
      const id = extractCreatedWorkspaceId(normalizedCreate);

      if (logoFile && id) {
        try {
          const md5 = await computeMd5HexFromBlob(logoFile);
          const photoRes = await uploadWorkspacePhoto({
            logical_name: imageLogicalName,
            filename: imageFileName,
            path: s3Path,
            file_type: logoFile.type,
            md5,
          });
          validateApiResponse(photoRes, t('list.createModal.submitError'));
        } catch {
          return {
            type: 'photo_error',
            workspaceId: id,
            message: t('list.createModal.logoUploadError'),
          };
        }
      }

      return { type: 'success', workspaceId: id };
    },
    [t],
  );

  const executeUpdate = useCallback(
    /**
     * @param {UpdateWorkspaceFormValues} values
     * @returns {Promise<WorkspaceSaveFlowSuccess | WorkspaceSaveFlowPhotoError>}
     */
    async (values) => {
      const {
        workspaceId,
        customerSlug,
        trimmedName,
        contactEmail,
        contactName,
        address,
        phoneNumber,
        logoFile,
      } = values;

      let imageFileName = '';
      let imageLogicalName = '';
      let s3Path = '';

      if (logoFile) {
        imageFileName = normalizeUploadFileName(logoFile.name);
        imageLogicalName = logicalNameFromUploadedFileName(imageFileName);

        const customer = slugForUpload(customerSlug, trimmedName);
        if (!customer) {
          throw new Error('Missing tenant slug for logo upload');
        }

        const uploadPayload = {
          customer,
          file_name: imageFileName,
          file_type: logoFile.type,
          file_route: TENANT_PHOTO_UPLOAD_FILE_ROUTE,
          overwrite: false,
        };
        const uploadRaw = await getUploadUrl(uploadPayload);
        validateApiResponse(uploadRaw, t('detail.overview.editModal.submitError'));
        const { uploadUrl, s3Path: resolvedS3Path } = parseSignedUploadResponse(uploadRaw);
        if (!resolvedS3Path || !String(resolvedS3Path).trim()) {
          throw new Error('Missing s3_path in upload URL response');
        }
        s3Path = String(resolvedS3Path).trim();

        await putFileToSignedUrl(uploadUrl, logoFile);
      }

      const slug = slugForUpload(customerSlug, trimmedName);
      const body = buildWorkspaceWriteBody({
        workspace_uuid: workspaceId,
        name: trimmedName,
        slug,
        contact_email: String(contactEmail ?? '').trim(),
        contact_name: String(contactName ?? '').trim(),
        phone_number: phoneNumber,
        address,
      });

      if (logoFile) {
        body.logical_name = imageLogicalName;
        body.filename = imageFileName;
        body.file_type = logoFile.type;
        body.path = s3Path;
      }

      const updateRes = await updateWorkspace(body);
      validateApiResponse(updateRes, t('detail.overview.editModal.submitError'));
      const id = workspaceId != null ? String(workspaceId) : null;

      if (logoFile && id) {
        try {
          const md5 = await computeMd5HexFromBlob(logoFile);
          const photoRes = await uploadWorkspacePhoto({
            logical_name: imageLogicalName,
            filename: imageFileName,
            path: s3Path,
            file_type: logoFile.type,
            md5,
          });
          validateApiResponse(photoRes, t('detail.overview.editModal.submitError'));
        } catch {
          return {
            type: 'photo_error',
            workspaceId: id,
            message: t('detail.overview.editModal.logoUploadError'),
          };
        }
      }

      return { type: 'success', workspaceId: id };
    },
    [t],
  );

  return { executeCreate, executeUpdate };
}
